import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import { useCallback } from 'react';

/**
 * Single source of truth for the four Smart Copy Amplitude event names.
 * Production call sites use the constants; tests intentionally encode the
 * literal strings as a regression check.
 */
export const SMART_COPY_EVENTS = {
  DROPDOWN_OPENED: 'Control Test Dropdown Opened Smart Copy Visible',
  TEMPLATE_SELECTED: 'Control Test Smart Copy Template Selected',
  SMART_COPY_SAVED: 'Control Test Smart Copy Saved',
  CONTROL_TEST_SAVED: 'Control Test Saved',
} as const;

export type SmartCopyEventName =
  (typeof SMART_COPY_EVENTS)[keyof typeof SMART_COPY_EVENTS];

/**
 * Thin Amplitude convenience for the Control Test Smart Copy feature —
 * every event carries tenant + role so Amplitude charts can segment by them;
 * caller-supplied values are overridden so the dimensions are always sourced
 * from the authenticated user.
 *
 * Intentionally NOT gated by the Smart Copy module flag. Callers own
 * gating so it stays visible at the call site.
 */
export const useSmartCopyAmplitude = () => {
  const amplitude = useAmplitude();
  const { user } = useRisksmartUser();

  return useCallback(
    (name: SmartCopyEventName, extraProps: Record<string, unknown>): void => {
      // Skip when user is unresolved — avoids polluting analytics with
      // `tenant: undefined` / `role: undefined`. Unreachable on Smart Copy
      // paths today (auth + modules hydrate before the dropdown can render)
      // but defensive against future entry points.
      if (!user?.claims_tenant || !user?.orgRole) {
        return;
      }
      amplitude.logEvent(name, {
        // Spread caller props first so `tenant` / `role` always win on key
        // collision — enforces the milestone AC #4 invariant that these
        // dimensions are sourced from the authenticated user, not the caller.
        ...extraProps,
        tenant: user.claims_tenant,
        role: user.orgRole,
      });
    },
    [amplitude, user?.claims_tenant, user?.orgRole]
  );
};
